import { apiConfig } from '../config/authConfig';
import type { 
  ApiResponse, 
  MeetingsListResponse, 
  MeetingDetailResponse,
  MeetingFilters 
} from '../types';

/**
 * API Service
 * Backend ile iletişimi sağlar
 */
class ApiService {
  private baseUrl: string;
  private accessToken: string | null = null;

  constructor() {
    this.baseUrl = apiConfig.baseUrl;
  }

  /**
   * Access token'ı ayarlar
   */
  setAccessToken(token: string) {
    this.accessToken = token;
  }

  /**
   * HTTP isteği yapar
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json();

      // Base64 encoded file response kontrolü (export endpoint'ten gelir)
      if (data.data && data.filename && data.contentType) {
        console.log('[API] Received base64 encoded file response');
        console.log('[API] Filename:', data.filename);
        console.log('[API] Content-Type:', data.contentType);

        // Base64'ü decode et
        const binaryString = atob(data.data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Blob oluştur
        const blob = new Blob([bytes], { type: data.contentType });
        console.log('[API] Created blob, size:', blob.size);

        // Blob'a filename ekle
        (blob as any).filename = data.filename;
        return { success: true, data: blob as unknown as T };
      }
      return data;
    } catch (error: any) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error.message || 'Bağlantı hatası',
        },
      };
    }
  }

  /**
   * Toplantıları listeler
   */
  async getMeetings(filters?: MeetingFilters, forceSync: boolean = false): Promise<ApiResponse<MeetingsListResponse>> {
    const params = new URLSearchParams();

    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.withTranscriptsOnly) params.append('withTranscriptsOnly', 'true');
    if (filters?.myMeetingsOnly) params.append('myMeetingsOnly', 'true');
    if (forceSync) params.append('forceSync', 'true');

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<MeetingsListResponse>(`/meetings${query}`);
  }

  /**
   * Toplantı detaylarını getirir
   * @param analyze - true ise AI analizi yapılır
   * @param reanalyze - true ise cache yoksayılır ve yeniden analiz yapılır
   */
  async getMeetingDetail(meetingId: string, analyze = false, reanalyze = false): Promise<ApiResponse<MeetingDetailResponse>> {
    const params = new URLSearchParams();
    if (analyze) params.append('analyze', 'true');
    if (reanalyze) params.append('reanalyze', 'true');
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<MeetingDetailResponse>(`/meetings/${meetingId}${query}`);
  }

  /**
   * Toplantı raporunu dışa aktarır
   */
  async exportMeeting(
    meetingId: string,
    format: 'pdf' | 'docx',
    includeTranscript = false
  ): Promise<ApiResponse<Blob>> {
    const params = new URLSearchParams();
    params.append('format', format);
    if (includeTranscript) params.append('includeTranscript', 'true');

    // meetingId'yi URL encode et (Base64 içerdiği için)
    const encodedMeetingId = encodeURIComponent(meetingId);
    return this.request<Blob>(`/meetings/${encodedMeetingId}/export?${params.toString()}`);
  }

  /**
   * Kullanıcı profilini getirir
   */
  async getUserProfile(): Promise<ApiResponse<{ user: { displayName: string; email: string } }>> {
    return this.request('/user/profile');
  }

  /**
   * Toplantı transkriptini email ile gönderir
   */
  async sendTranscriptEmail(
    meetingId: string,
    contentType: 'full' | 'summary',
    recipientType: 'internal' | 'external' | 'all' | 'custom',
    customRecipients?: string[]
  ): Promise<ApiResponse<{ success: boolean; message: string; recipients: string[] }>> {
    return this.request('/send-transcript-email', {
      method: 'POST',
      body: JSON.stringify({
        meetingId,
        contentType,
        recipientType,
        customRecipients
      })
    });
  }
}

// Singleton instance
export const apiService = new ApiService();
